'use client';

import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTransition } from 'react';
import { Send } from 'lucide-react';

import { Button } from '@/slw/shared/ui/button';
import FormProvider, { Input, Textarea, Select } from '@/slw/shared/ui/form';
import { FeedbackFormValues, feedbackSchema } from '@/slw/entities/contact-us/contact-us.schema';
import { useSubmitFeedbackMutation } from '@/slw/entities/contact-us/contact-us.mutation';
import { useGetFeedbackTopics } from '@/slw/entities/contact-us/contact-us.query';

type FeedbackTopic = {
  id: number;
  name: string;
};

const defaultValues: FeedbackFormValues = {
  topicId: '',
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  companyName: '',
  message: '',
};

export function FeedbackForm() {
  const methods = useForm<FeedbackFormValues>({
    resolver: zodResolver(feedbackSchema),
    defaultValues,
  });
  const { data: feedbackTopics } = useGetFeedbackTopics();

  const submitFeedback = useSubmitFeedbackMutation();
  const [isPending, startTransition] = useTransition();

  const onSubmit = (data: FeedbackFormValues) => {
    try {
      startTransition(async () => {
        await submitFeedback.mutateAsync({
          ...data,
          topicId: data.topicId,
          topicName: (feedbackTopics as any)?.find((topic: FeedbackTopic) => topic.id === Number(data.topicId))?.name,
          companyName: data.companyName ?? '',
        });
        methods.reset();
      });
    } catch (err) {
      // Error handled
    }
  };

  return (
    <FormProvider {...methods} onSubmit={onSubmit}>
      <h2 id='feedback-form-title' className='text-[2rem] leading-[100%] font-bold lg:mb-15 mb-12'>
        Leave us your feedback
      </h2>
      {/* Left column */}
      <div className='grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-10'>
        <div className='space-y-4'>
          <div>
            <Select
              name='topicId'
              label='Feedback topic'
              direction='column'
              required
              options={
                (feedbackTopics as any)?.map((topic: FeedbackTopic) => ({
                  label: topic.name,
                  value: topic.id.toString(),
                })) ?? []
              }
              placeholder='Select topic'
            />
          </div>
          <div>
            <Textarea
              label='Please leave your detailed feedback below'
              name='message'
              direction='column'
              rows={7}
              required
              placeholder='Enter your feedback'
              className='block w-full'
            />
          </div>
        </div>
        {/* Right column */}
        <div className='space-y-4'>
          <div>
            <Input direction='column' label='First name' name='firstName' required />
          </div>
          <div>
            <Input direction='column' label='Last name' name='lastName' required />
          </div>
          <div>
            <Input direction='column' label='Email Address' name='email' type='email' required />
          </div>
          <div>
            <Input direction='column' label='Phone Number' name='phone' />
          </div>
          <div>
            <Input direction='column' label='Company name' name='companyName' />
          </div>
        </div>
      </div>
      <div className='md:w-1/2 mt-4 md:pl-5  md:ml-auto'>
        <Button type='submit' variant='default' color='primary' className='gap-4 w-full' disabled={isPending}>
          {isPending ? (
            <span>Submitting...</span>
          ) : (
            <>
              <Send className='w-5 h-5 mr-2' />
              SUBMIT FEEDBACK
            </>
          )}
        </Button>
      </div>
    </FormProvider>
  );
}
